import {inject, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {Observable} from 'rxjs';
import {CapabilitiesService} from './capabilities.service';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private readonly tokenStorageKey: string = 'hs_token';
  private capabilitiesService: CapabilitiesService = inject(CapabilitiesService);

  private token: WritableSignal<string> = signal('');
  private isOneTimeUse: WritableSignal<boolean> = signal(false);
  private tokenStateChange: WritableSignal<'set' | 'cleared' | 'loaded' | null> = signal(null);

  public accessToken: Signal<string> = this.token.asReadonly();
  public tokenState: Signal<'set' | 'cleared' | 'loaded' | null> = this.tokenStateChange.asReadonly();

  public getToken(): string {
    if(!this.isOneTimeUse()) {
      const cookieToken = this.getTokenFromCookies();
      const localStorageToken = this.getTokenFromLocalStorage();
      const persistedToken = cookieToken.trim() !== '' ? cookieToken : localStorageToken;
      if(this.token().trim() === '' && persistedToken.trim() !== '') {
        // Check if token is a JWT (old format) and silently remove it
        if(this.isJWTToken(persistedToken)) {
          console.warn('Detected old JWT token, removing from storage');
          this.deleteTokenFromCookies();
          this.deleteTokenFromLocalStorage();
          return '';
        }
        this.token.set(persistedToken);
        this.tokenStateChange.set('loaded');
      }
    }
    return this.token();
  }

  public hasToken(): boolean {
    return this.getToken().trim() !== '';
  }

  public getValidatedToken(): Observable<string> {
    return new Observable<string>((observer) => {
      const token = this.getToken();
      if(token.trim() !== '') {
        observer.next(token);
      } else {
        observer.error('No valid token found');
      }
      observer.complete();
    });
  }

  public setToken(newToken: string, isOneTimeUse: boolean = false): void {
    this.isOneTimeUse.set(isOneTimeUse);
    if(newToken.trim() === '') {
      console.error('The given token to set is empty.');
      return;
    }
    // Check if token is a JWT (old format) and reject it
    if(this.isJWTToken(newToken)) {
      console.error('Cannot set JWT token, only database tokens are supported');
      return;
    }
    this.token.set(newToken);
    if(!this.isOneTimeUse()) {
      this.storeTokenToCookies(newToken);
      this.storeTokenToLocalStorage(newToken);
      this.tokenStateChange.set('set');
    }
  }

  public clearToken(): void {
    this.token.set('');
    if(!this.isOneTimeUse()) {
      this.deleteTokenFromCookies();
      this.deleteTokenFromLocalStorage();
      this.tokenStateChange.set('cleared');
    }
    this.capabilitiesService.resetCapabilities();
  }

  //#region token validation
  /**
   * Checks if a token is a JWT token (old format)
   * JWT tokens have 3 parts separated by dots and typically start with 'eyJ'
   */
  private isJWTToken(token: string): boolean {
    if(!token || token.trim() === '') {
      return false;
    }
    const parts = token.split('.');
    return parts.length === 3 && token.startsWith('eyJ');
  }

  /**
   * Checks if a token is a database token (new format)
   * Database tokens have the format: number|string
   */
  private isDatabaseToken(token: string): boolean {
    if(!token || token.trim() === '') {
      return false;
    }
    const pattern = /^\d+\|[a-zA-Z0-9]+$/;
    return pattern.test(token);
  }
  //#endregion token validation

  //#region cookie handling
  private storeTokenToCookies(token: string): void {
    const secureAttribute = this.isHttpsContext() ? '; Secure' : '';
    // Database tokens don't expire, set a long max-age (1 year)
    const maxAge = 365 * 24 * 60 * 60;
    document.cookie = `token=${encodeURIComponent(token)}; path=/; max-age=${maxAge}; SameSite=Lax${secureAttribute}`;
  }

  private deleteTokenFromCookies(): void {
    const secureAttribute = this.isHttpsContext() ? '; Secure' : '';
    document.cookie = `token=; path=/; max-age=0; SameSite=Lax${secureAttribute}`;
  }

  private getTokenFromCookies(): string  {
    const match = document.cookie.match(/(^|;\s*)token=([^;]*)/);
    return match ? decodeURIComponent(match[2]) : '';
  }

  //#endregion cookie handling

  //#region localStorage handling
  private storeTokenToLocalStorage(token: string): void {
    try {
      localStorage.setItem(this.tokenStorageKey, token);
    } catch (error) {
      console.warn('Failed to write token to localStorage:', error);
    }
  }

  private deleteTokenFromLocalStorage(): void {
    try {
      localStorage.removeItem(this.tokenStorageKey);
    } catch (error) {
      console.warn('Failed to remove token from localStorage:', error);
    }
  }

  private getTokenFromLocalStorage(): string {
    try {
      return localStorage.getItem(this.tokenStorageKey) ?? '';
    } catch (error) {
      console.warn('Failed to read token from localStorage:', error);
      return '';
    }
  }
  //#endregion localStorage handling

  private isHttpsContext(): boolean {
    return window.location.protocol === 'https:';
  }


}
